import { forwardRef, useContext, useImperativeHandle, useState } from 'react'
import { observer } from 'mobx-react'

import * as Cesium from '@cesiumgs/cesium-analytics'
import {
  Button,
  Col,
  InputNumber,
  Row,
  message,
} from 'antd'
import SlopeDirectionResult from './result'
import {
  getArea,
  getPolygonEdgeMidlePoint,
  getPolygonVertex,
  getSlope,
  splitPolygonToGridAsync,
} from '@/components/Cesium/Turf/TurfUtils'
import { IgetPositionPUN } from '@/service/analysis'
import useStore from '@/hooks/useStore'

import Modal from '@/components/Modal'
import Loading from '@/components/Content/RightNavCtrl/loading'

import Draw from '@/components/Cesium/Draw/Draw'
import { DrawType } from '@/components/Cesium/Draw/DrawType'
import { DrawEventType } from '@/components/Cesium/Draw/DrawEventType'
import { rightNavContext } from '@/components/Content/RightNavCtrl/RightNavCtrl'
import { AN1_MODAL_RESULT_TITLE, MODAL_WIDTH600, MODAL_WIDTH_AN1 } from '@/components/Content/RightNavCtrl/data'
import './index.less'

const CUSTOM_DATA_SOURCE = 'slopAnalysisDirectionEntity'
const POLYGON_ENTITY = 'polygonEntityDirection'
const DRAW_SLOPE_RECTANGE = 'drawSlopeRectange'
const dataSource: any = null
// let addLineEntity: any = null
let polyEntity: any = null
const prefix = 'slopeDirectionAnalysis'
const slopeDirectionAnalysis = forwardRef((props, ref) => {
  const uiStore = useStore('uiStore')
  const rightNavCtrlStore = useStore('rightNavCtrlStore')
  const rightContext = useContext(rightNavContext)
  const { viewer } = rightContext
  const [inputValue, setInputValue] = useState(50)
  const [disabledRun, setDisabledRun] = useState(true)
  const [isShowResult, setIsShowResult] = useState(false)
  const [polygonPosition, setPolygonPosition] = useState<any>([])
  const [polygonSource8, setPolygonSource8] = useState<any>([])
  const [isLoading, setIsLoading] = useState(false)

  useImperativeHandle(ref, () => ({
    handleClearAll,
  }))

  const inputChange = (value: number | null) => {
    setInputValue(value || 0)
  }

  const transformCartesianToWGS84 = (cartesian: any) => {
    const ellipsoid = Cesium.Ellipsoid.WGS84
    const cartographic = ellipsoid.cartesianToCartographic(cartesian)
    return {
      lng: Cesium.Math.toDegrees(cartographic.longitude),
      lat: Cesium.Math.toDegrees(cartographic.latitude),
      alt: cartographic.height,
    }
  }
  const drawPolygon = () => {
    handleClearAll()
    const drawTool = new Draw(viewer)
    const dataSource = new Cesium.CustomDataSource(CUSTOM_DATA_SOURCE)
    rightNavCtrlStore.setSlopDirectionAnalysisDataSource(dataSource)
    viewer.dataSources.add(dataSource)
    const action = drawTool.create(DrawType.Polygon, {
      useDefaultEntity: true,
    })
    action.on(DrawEventType.Complete, (evt) => {
      if (evt) {
        const addEntity = new Cesium.Entity({
          id: POLYGON_ENTITY,
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(evt),
            material: Cesium.Color.RED.withAlpha(0.5),
          },
        })
        rightNavCtrlStore.slopDirectionAnalysisDataSource && rightNavCtrlStore.slopDirectionAnalysisDataSource.entities.add(addEntity)
        polyEntity = addEntity
        setDisabledRun(false)
        const point: any = []
        evt.forEach((item: any) => {
          const p = transformCartesianToWGS84(item)
          point.push([p.lng, p.lat])
        })
        point.push(point[0])
        const polygon = {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [point],
          },
        }
        setPolygonPosition([polygon])
      }
      action.destroy()
    })
  }

  function getHeightByLonLat(lon: number, lat: number) {
    return viewer.scene.sampleHeight(
      new Cesium.Cartographic(
        Cesium.Math.toRadians(lon),
        Cesium.Math.toRadians(lat),
      ),
    )
  }

  function calculateSlopeColor(value: number, alpha: number) {
    let color = `rgba(221, 243, 209,${alpha})`
    let index = 0
    const degrees = value * (180 / Math.PI)
    if (value < 0.17453292519943295) {
      color = `rgba(0, 255, 0,${alpha})`
      index = 0
    }
    else if (value < 0.2617993877991494) {
      color = `rgba(173, 255, 47,${alpha})`
      index = 1
    }
    else if (value < 0.3490658503988659) {
      color = `rgba(154, 205, 50,${alpha})`
      index = 2
    }
    else if (value < 0.4363323129985824) {
      color = `rgba(255, 255, 0,${alpha})`
      index = 3
    }
    else if (value < 0.5235987755982988) {
      color = `rgba(255, 215, 0,${alpha})`
      index = 4
    }
    else if (value < 0.6108652381980153) {
      color = `rgba(255, 99, 71,${alpha})`
      index = 5
    }
    else if (value < 0.6981317007977318) {
      color = `rgba(255, 0, 0,${alpha})`
      index = 6
    }
    else {
      color = `rgba(165, 42, 42,${alpha})`
      index = 7
    }
    return { color, degrees, index }
  }

  function createPolygonInsrance(points: any, color: any) {
    const polygon = new Cesium.PolygonGeometry({
      polygonHierarchy: new Cesium.PolygonHierarchy(
        Cesium.Cartesian3.fromDegreesArray(points),
      ),
    })

    const polygonInstance = new Cesium.GeometryInstance({
      geometry: polygon,
      attributes: {
        color: Cesium.ColorGeometryInstanceAttribute.fromColor(
          Cesium.Color.fromCssColorString(color),
        ),
      },
    })
    return polygonInstance
  }

  async function getPositionPUN(payload: any, data8: any) {
    try {
      const response: any = await IgetPositionPUN(payload)
      if (
        response.data
        && response.data.returnCode === '200'
        && response.data.contents
        && response.data.contents.result
        && response.data.contents.result.jusoList
        && response.data.contents.result.jusoList.jusoList
        && response.data.contents.result.jusoList.jusoList.jibunAddr
      ) {
        const pnu = response.data.contents.result.jusoList.jusoList
        data8[8].position = pnu.jibunAddr
        setPolygonSource8(data8)
        setIsShowResult(true)
      }
    }
    finally {
      setIsLoading(false)
      setIsShowResult(true)
    }
  }

  const runAnalysisFn = () => {
    polygonPosition.forEach((item: any) => {
      splitPolygonToGridAsync(item, inputValue, {
        units: 'meters',
        autoScaleToFitRect: true,
      } as any)?.then((res: any) => {
        if (res && res.features && res.features.length > 0) {
          if (res.features.length > 10000) {
            message.warning(`${res.features.length}planes are being analyzed, over drawing limit!`)
            return
          }
          setIsLoading(true)
          const instances: any = []
          const analysis: any = [[], [], [], [], [], [], [], []]
          const backData = [
            {
              key: '1',
              range: '10° 미만',
              color: '#D8F4CE',
              area: 0,
              percentage: '',
            },
            {
              key: '2',
              range: '10°이상 15°미만',
              color: '#B8E4C3',
              area: 151.51,
              percentage: '8.02',
            },
            {
              key: '3',
              range: '15°이상 20°미만',
              color: '#98D1BA',
              area: 247.19,
              percentage: '13.08',
            },
            {
              key: '4',
              range: '20°이상 25°미만',
              color: '#A6DDDA',
              area: 300.17,
              percentage: '15.88',
            },
            {
              key: '5',
              range: '25°이상 30°미만',
              color: '#9EE0F8',
              area: 358.5,
              percentage: '18.97',
            },
            {
              key: '6',
              range: '30°이상 35°미만',
              color: '#91D0F9',
              area: 100.0,
              percentage: '5.29',
            },
            {
              key: '7',
              range: '35°이상 40°미만',
              color: '#80A1D7',
              area: 284.77,
              percentage: '15.07',
            },
            {
              key: '8',
              range: '40°이상',
              color: '#BAA3D7',
              area: 0,
              percentage: '0',
            },
            {
              key: '9',
              range: '계',
              color: '',
              area: 1889.97,
              percentage: '100%',
              avgDegrees: '10°',
              position: '',
            },
          ]
          let totalArea = 0
          let avgDegrees = 0
          const degreesArr: any = []
          res.features.forEach((item: any, index: number) => {
            const res = getPolygonEdgeMidlePoint(item)
            const polygonPo = getPolygonVertex(item)
            const area = getArea(item)
            const polygonArr: any = []
            // if (polygonPo.features.length < 4) {
            //   return
            // }

            polygonPo.features.forEach((item) => {
              polygonArr.push(item.geometry.coordinates[0])
              polygonArr.push(item.geometry.coordinates[1])
            })

            const pointArr: any = []
            res.features.forEach((child, index) => {
              const height = getHeightByLonLat(
                child.geometry.coordinates[0],
                child.geometry.coordinates[1],
              )
              const obj = {
                x: child.geometry.coordinates[0],
                y: child.geometry.coordinates[1],
                z: height,
              }
              pointArr.push(obj)
            })
            const slope1 = getSlope(pointArr[0], pointArr[4])
            const slope2 = getSlope(pointArr[1], pointArr[5])
            const slope3 = getSlope(pointArr[2], pointArr[6])
            const slope4 = getSlope(pointArr[3], pointArr[7])
            const avge
              = (Math.abs(slope1)
                + Math.abs(slope2)
                + Math.abs(slope3)
                + Math.abs(slope4))
              / 4
            const arrposition = [
              Math.abs(slope1),
              Math.abs(slope2),
              Math.abs(slope3),
              Math.abs(slope4),
            ] //
            arrposition.sort()
            const maxslope = arrposition[3]
            const lineposition = []
            if (maxslope === Math.abs(slope1)) {
              if (slope1 > 0) {
                lineposition.push(
                  pointArr[0].x,
                  pointArr[0].y,
                  pointArr[4].x,
                  pointArr[4].y,
                )
              }
              else {
                lineposition.push(
                  pointArr[4].x,
                  pointArr[4].y,
                  pointArr[0].x,
                  pointArr[0].y,
                )
              }
            }
            else if (maxslope === Math.abs(slope2)) {
              if (slope2 > 0) {
                lineposition.push(
                  pointArr[1].x,
                  pointArr[1].y,
                  pointArr[5].x,
                  pointArr[5].y,
                )
              }
              else {
                lineposition.push(
                  pointArr[5].x,
                  pointArr[5].y,
                  pointArr[1].x,
                  pointArr[1].y,
                )
              }
            }
            else if (maxslope === Math.abs(slope3)) {
              if (slope3 > 0) {
                lineposition.push(
                  pointArr[2].x,
                  pointArr[2].y,
                  pointArr[6].x,
                  pointArr[6].y,
                )
              }
              else {
                lineposition.push(
                  pointArr[6].x,
                  pointArr[6].y,
                  pointArr[2].x,
                  pointArr[2].y,
                )
              }
            }
            else if (maxslope === Math.abs(slope4)) {
              if (slope4 > 0) {
                lineposition.push(
                  pointArr[3].x,
                  pointArr[3].y,
                  pointArr[7].x,
                  pointArr[7].y,
                )
              }
              else {
                lineposition.push(
                  pointArr[7].x,
                  pointArr[7].y,
                  pointArr[3].x,
                  pointArr[3].y,
                )
              }
            }
            const obj = calculateSlopeColor(avge, 0.7)
            const curPolygonInstance = createPolygonInsrance(
              polygonArr,
              obj.color,
            )
            analysis[obj.index].push({
              area,
              degrees: obj.degrees,
            })
            instances.push(curPolygonInstance)
            totalArea += area
            avgDegrees += obj.degrees
            // degreesArr.push(Number(obj.degrees.toFixed(2)))
            viewer.entities.add({
              name: DRAW_SLOPE_RECTANGE,
              polyline: {
                clampToGround: true,
                positions: Cesium.Cartesian3.fromDegreesArray(lineposition),
                material: new Cesium.PolylineArrowMaterialProperty(
                  Cesium.Color.fromCssColorString('#ffd040'),
                ),
                width: 15,
              },
            })
          })
          analysis.forEach((child: any, index: number) => {
            let curArea = 0
            child.forEach((item: any) => {
              curArea += item.area
            })
            const percent = Number(curArea / totalArea) * 100
            backData[index].area = Number(curArea.toFixed(2))
            backData[index].percentage = `${percent.toFixed(2)}%`
          })

          backData[8].area = Number(totalArea.toFixed(2))
          backData[8].percentage = '100%'
          backData[8].avgDegrees = (avgDegrees / res.features.length).toFixed(2)

          const pos = {
            lng: res.features[0].geometry.coordinates[0][0][0],
            lat: res.features[0].geometry.coordinates[0][0][1],
          }
          setPolygonSource8(backData)
          // addLineEntity = newPolyPrimitiveSource
          getPositionPUN(pos, backData)
        }
        else {
          message.warning('범위 표면이 잘못 되었거나 거리가 너무 멉니다.')
        }
      })
    })
  }

  const renderModalTitle = () => {
    return AN1_MODAL_RESULT_TITLE
  }

  const renderModalWidth = () => {
    return MODAL_WIDTH600
  }

  const handleClearAll = () => {
    if (rightNavCtrlStore.slopDirectionAnalysisDataSource) {
      rightNavCtrlStore.slopDirectionAnalysisDataSource.entities.removeAll()
    }

    removeNameEntities()
    setDisabledRun(true)
  }

  const removeNameEntities = () => {
    viewer.entities.removeAll()
    if (polyEntity) {
      polyEntity.show = true
    }
  }

  return (
    <div className={`${prefix}`}>
      <Row className={`${prefix}-one`}>
        <strong>거리</strong>
        <Col className={`${prefix}-one-input`}>
          <InputNumber min={0} value={inputValue} onChange={inputChange} />
          <span>m</span>
        </Col>
      </Row>
      <Row className={`${prefix}-three`}>
        <span>영역 지정</span>
        <span>* 분석할 영역을 지도에서 그리세요</span>
      </Row>
      <Row className={`${prefix}-btn`}>
        <Button onClick={drawPolygon} className={`${prefix}-btn-sure`}>
          영역 지정
        </Button>
        <Button onClick={handleClearAll} disabled={disabledRun} className={`${prefix}-btn-cancel`}>
          지정 취소
        </Button>
      </Row>
      <Row className={`${prefix}-bottom`}>
        <Button onClick={runAnalysisFn} disabled={disabledRun}>
          분석 실행
        </Button>
      </Row>
      <Modal
        visible={isShowResult}
        headerTitle={renderModalTitle()}
        handleClose={() => {
          setIsShowResult(false)
          removeNameEntities()
        }}
        styles={{
          position: 'fixed',
          width: renderModalWidth(),
          top: `${window.SystemConfig.uiConfig.headerHeight + 30}px`,
          left: `${uiStore.Content_Left_Width + MODAL_WIDTH_AN1 + 30 + 30}px`,
          zIndex: 10,
        }}
      >
        <SlopeDirectionResult polygonSource8={polygonSource8} />
      </Modal>
      {isLoading && <Loading></Loading>}
    </div>
  )
})
export default observer(slopeDirectionAnalysis)
